/**
 * Read-only guard for amfori-imported audits & findings (US3.4 / DELTA-2171).
 *
 * amfori owns these rows: a user editing or deleting one would either be
 * overwritten by, or drift from, the next sync. Every backend mutation entry
 * point that touches `audits_service_audits` / `audits_service_audit_findings`
 * (and the digitized datapoints derived from an amfori audit) runs the candidate
 * row through the matching guard and, when it is amfori-sourced, returns this
 * typed 403 instead of writing. The guard is **unconditional** — no role,
 * impersonation flag, or admin escape hatch.
 *
 * Provenance is read from the `external_source` column (the integration we pulled
 * the row from), resolved at write time and never trusted from the request. The
 * returned shape is structurally a backend `IErrorDefinition` / legacy `APIError`
 * param, so call sites just do `reply.apiError(err)` (modern) or
 * `new APIError(err)` (legacy `2025-06`).
 */

/** Error `code` the frontend keys on to render the read-only toast (sub-task 3). */
export const AMFORI_AUDIT_READ_ONLY_CODE = 'amfori_audit_read_only';

/**
 * Integration marker the amfori importer stamps on
 * `audits_service_audits.external_source` — the integration we connected to, not
 * the audit type (that stays in `audit_type_id`).
 */
export const AMFORI_EXTERNAL_SOURCE = 'AMFORI';

/**
 * `external_source` values amfori writes on `audits_service_audit_findings`
 * (BSCI findings + zero-tolerance incidents). Pre-existing US3.4 findings
 * convention — it encodes integration+type, unlike the audits column.
 */
export const AMFORI_FINDING_EXTERNAL_SOURCES = [
  'AMFORI_BSCI',
  'AMFORI_ZT',
] as const;

/** Structured error returned by the guards; assignable to `IErrorDefinition` / `APIError` params. */
export interface AmforiReadOnlyError {
  status: 403;
  code: typeof AMFORI_AUDIT_READ_ONLY_CODE;
  message: string;
}

/** A row carrying an `external_source` marker, camelCase (modern) or snake_case (legacy). */
export interface ExternalSourceRow {
  externalSource?: string | null;
  external_source?: string | null;
}

const externalSourceOf = (row: ExternalSourceRow): string | null =>
  row.externalSource ?? row.external_source ?? null;

/** True when the audit was imported from amfori (`external_source = 'AMFORI'`). */
export const isAmforiSourcedAudit = (row: ExternalSourceRow): boolean =>
  externalSourceOf(row) === AMFORI_EXTERNAL_SOURCE;

/** True when the finding was imported from amfori (BSCI finding or zero-tolerance incident). */
export const isAmforiSourcedFinding = (row: ExternalSourceRow): boolean =>
  AMFORI_FINDING_EXTERNAL_SOURCES.includes(
    externalSourceOf(row) as (typeof AMFORI_FINDING_EXTERNAL_SOURCES)[number],
  );

const readOnlyError = (subject: 'audit' | 'finding'): AmforiReadOnlyError => ({
  status: 403,
  code: AMFORI_AUDIT_READ_ONLY_CODE,
  message: `This ${subject} was imported from amfori and is read-only.`,
});

/** Returns the read-only error iff the audit is amfori-sourced, else `null`. */
export const guardAmforiAuditReadOnly = (
  audit: ExternalSourceRow,
): AmforiReadOnlyError | null =>
  isAmforiSourcedAudit(audit) ? readOnlyError('audit') : null;

/** Returns the read-only error iff the finding is amfori-sourced, else `null`. */
export const guardAmforiFindingReadOnly = (
  finding: ExternalSourceRow,
): AmforiReadOnlyError | null =>
  isAmforiSourcedFinding(finding) ? readOnlyError('finding') : null;
